import { INITIAL_FILTER_STATE } from './constants';
import {
  type ActiveBrandIds,
  type ActiveColours,
  type ActiveConditions,
  type ActiveFilterValues,
  type ActiveSizeIds,
  type URLFilterParams,
} from './types';

export class SearchUrlHelpers {
  public getFiltersFromQueryParams = (urlParams: URLFilterParams) => {
    const {
      groups,
      productTypes,
      gender,
      isKids,
      sizes,
      brands,
      isDiscounted,
      priceMin,
      priceMax,
      colours,
      conditions,
      sort,
    } = urlParams;

    return {
      ...this.getCategoriesFromQuery(
        INITIAL_FILTER_STATE.activeGroup,
        INITIAL_FILTER_STATE.activeProductTypes,
        groups,
        productTypes
      ),
      ...this.getSizesFromQuery(INITIAL_FILTER_STATE.activeSizeIds, sizes),
      ...this.getBrandsFromQuery(INITIAL_FILTER_STATE.activeBrandIds, brands),
      ...this.getColoursFromQuery(INITIAL_FILTER_STATE.activeColours, colours),
      ...this.getConditionsFromQuery(
        INITIAL_FILTER_STATE.activeConditions,
        conditions
      ),
      isOnSale: Boolean(isDiscounted),
      isKids: Boolean(isKids),
      activePrice: {
        min: priceMin ? Number(priceMin) : null,
        max: priceMax ? Number(priceMax) : null,
      },
      gender: gender ? gender : undefined,
      sortBy: sort || INITIAL_FILTER_STATE.sortBy,
    };
  };

  public getQueryParamsFromFilters = (
    searchParams: URLSearchParams,
    filterValues: Partial<ActiveFilterValues>
  ): URLSearchParams => {
    const {
      activeGroup,
      activeProductTypes,
      activeGender,
      activeSizeIds,
      activeBrandIds,
      isKids,
      isOnSale,
      activePrice,
      activeColours,
      activeConditions,
      sortBy,
    } = filterValues;

    if (activeGroup) {
      searchParams.set('groups', String(activeGroup));
    }

    if (activeProductTypes && activeProductTypes.length > 0) {
      searchParams.set('productTypes', activeProductTypes.join(','));
    }

    if (activeGender) {
      searchParams.set('gender', String(activeGender));
    }

    if (typeof isKids !== 'undefined') {
      searchParams.set('isKids', String(isKids));
    }

    if (activeBrandIds && activeBrandIds.length > 0) {
      searchParams.set('brands', activeBrandIds.join(','));
    }

    if (activeSizeIds && activeSizeIds.length > 0) {
      const sizes = activeSizeIds.join(',');
      searchParams.set('sizes', sizes);
    }

    if (activePrice && activePrice.min) {
      searchParams.set('priceMin', String(activePrice.min));
    }

    if (activePrice && activePrice.max) {
      searchParams.set('priceMax', String(activePrice.max));
    }

    if (isOnSale) {
      searchParams.set('isDiscounted', String(isOnSale));
    }

    if (activeColours && activeColours.length > 0) {
      searchParams.set('colours', activeColours.join(','));
    }

    if (activeConditions && activeConditions.length > 0) {
      searchParams.set('conditions', activeConditions.join(','));
    }

    if (sortBy) {
      searchParams.set('sort', String(sortBy));
    }

    return searchParams;
  };

  private getCategoriesFromQuery = (
    initialActiveGroup: string,
    initialProductTypes: string[],
    activeGroup?: string,
    activeProductTypes?: string
  ) => {
    if (!activeGroup && !activeProductTypes) {
      return {
        activeGroup: initialActiveGroup,
        activeProductTypes: initialProductTypes,
      };
    }
    const activeProductTypeQueryOrReset: string[] = [];

    if (activeProductTypes) {
      activeProductTypes
        .split(',')
        .forEach((subcat) => activeProductTypeQueryOrReset.push(subcat));
    }
    return {
      activeGroup: activeGroup ? activeGroup : initialActiveGroup,
      activeProductTypes: activeProductTypeQueryOrReset,
    };
  };

  private getBrandsFromQuery = (
    initialActiveBrands: ActiveBrandIds,
    activeBrandIds?: string
  ) => {
    if (!activeBrandIds) {
      return {
        activeBrandIds: initialActiveBrands,
      };
    }
    const brandsQuery: number[] = [];
    activeBrandIds.split(',').forEach((id) => brandsQuery.push(Number(id)));
    return {
      activeBrandIds: brandsQuery,
    };
  };

  private getSizesFromQuery = (
    initialActiveSizeIds: ActiveSizeIds,
    sizes?: string
  ): { activeSizeIds: ActiveSizeIds } => {
    if (!sizes) {
      return { activeSizeIds: initialActiveSizeIds };
    }

    return { activeSizeIds: sizes.split(',') };
  };

  private getColoursFromQuery = (
    initialColours: ActiveColours,
    colours?: string
  ): { activeColours: ActiveColours } => {
    if (!colours) {
      return {
        activeColours: initialColours,
      };
    }

    return {
      activeColours: colours.split(','),
    };
  };

  private getConditionsFromQuery = (
    initialConditions: ActiveConditions,
    conditions?: string
  ): { activeConditions: ActiveConditions } => {
    if (!conditions) {
      return {
        activeConditions: initialConditions,
      };
    }

    return {
      activeConditions: conditions.split(','),
    };
  };
}
